import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  LinearProgress,
  Alert,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  Snackbar
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/RefreshOutlined';
import SuggestIcon from '@mui/icons-material/ChecklistOutlined';
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import ExportIcon from '@mui/icons-material/ImportExportOutlined';
import MoreIcon from '@mui/icons-material/MoreVertOutlined';
import GenerateIcon from '@mui/icons-material/PlaylistAddOutlined';
import { useMutation } from '@apollo/client';
import {
  GENERATE_TASKS,
  PREVIEW_ENHANCE_TASKS,
  COMMIT_ENHANCE_TASKS
} from '../../graphql/mutations';
import { generateProjectZip } from '../../graphqlService';
import { useAuth } from '../../context/AuthContext';
import { debugLog } from '../../utils/debugLog';
import { getApolloErrorMessage } from '../../utils/apolloError';
import { formatPriorityLabel } from '../../utils/projectStatus';
import { taskTypeLabel } from '../EditorWorkspace/taskMeta';
import { brand } from '../../theme';
import { downloadTextFile } from '../../utils/download';
import { formatTimecode } from '../../utils/timecode';

interface TaskManagementPanelProps {
  project: any;
  editable: boolean;
  /** The project is delivered or cancelled: keep downloads and exports, but
   *  regenerating and AI enhancement are disabled. */
  readOnly?: boolean;
  /** Open the AI preview once tasks exist (the editor dashboard's "Suggest
   *  task guidance" action routes here instead of applying changes blind). */
  autoOpenAIPreview?: boolean;
  onAutoOpenAIPreviewHandled?: () => void;
  /** Tasks are being generated from the feedback right now (or are about to
   *  be): an auto-open waits for them rather than previewing an empty list. */
  tasksPending?: boolean;
  onTasksUpdated?: () => void;
  /** `toolbar` renders just the actions, for the editor workspace's Tasks tab. */
  variant?: 'panel' | 'toolbar';
}

interface Notice {
  message: string;
  severity: 'success' | 'info';
}

interface AIPreviewData {
  suggestions: any[];
  raw: string;
}

type AIDialogState =
  | { status: 'closed' }
  | { status: 'loading' }
  | { status: 'ready' }
  | { status: 'empty'; message: string }
  | { status: 'error'; message: string };

export const AI_PREVIEW_LOADING_TEXT = 'Preparing suggestions…';

export default function TaskManagementPanel({
  project,
  editable,
  readOnly = false,
  autoOpenAIPreview = false,
  onAutoOpenAIPreviewHandled,
  tasksPending = false,
  onTasksUpdated,
  variant = 'panel'
}: TaskManagementPanelProps) {
  const { user } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [aiDialog, setAiDialog] = useState<AIDialogState>({ status: 'closed' });
  const [aiPreviewData, setAiPreviewData] = useState<AIPreviewData | null>(null);
  const [regenerateConfirmOpen, setRegenerateConfirmOpen] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const autoOpenHandledRef = useRef(false);
  // Whether the generate that is running replaces tasks or creates the first ones.
  const replacingRef = useRef(true);
  // Ignore a preview that finishes after its dialog was closed (or reopened).
  const aiRequestRef = useRef(0);

  // Regenerating and AI enhancement change the project's tasks.
  const canChangeTasks = editable && !readOnly;
  const setSuccessMessage = (message: string) => setNotice({ message, severity: 'success' });
  const taskCount: number = Array.isArray(project?.tasks) ? project.tasks.length : 0;

  const [generateTasks, { loading: generating }] = useMutation(GENERATE_TASKS, {
    onCompleted: (data) => {
      const result = data?.generateProjectTasks;
      debugLog('GENERATE_TASKS mutation completed:', {
        success: result?.success,
        tasksGenerated: result?.tasksGenerated,
        message: result?.message,
        returnedTasks: result?.tasks?.length ?? 'no tasks field'
      });
      setRegenerateConfirmOpen(false);

      // The resolver reports failures as { success: false, message } rather
      // than a GraphQL error, so onCompleted runs for those too. It used to
      // announce "Successfully regenerated 0 tasks" whatever happened.
      if (!result?.success) {
        setErrorMessage(result?.message || 'Tasks could not be regenerated. Please try again.');
        // The list may or may not have been replaced; reload rather than guess.
        onTasksUpdated?.();
        return;
      }

      const tasksCount = typeof result.tasksGenerated === 'number'
        ? result.tasksGenerated
        : (result.tasks?.length ?? 0);
      if (tasksCount === 0) {
        setNotice({
          message: 'Regeneration finished, but no tasks could be generated from the annotations.',
          severity: 'info'
        });
      } else {
        setSuccessMessage(replacingRef.current
          ? `Regenerated ${tasksCount} task${tasksCount !== 1 ? 's' : ''} from annotations`
          : `Generated ${tasksCount} task${tasksCount !== 1 ? 's' : ''} from the feedback`);
      }
      onTasksUpdated?.();
    },
    onError: (error) => {
      console.error('Error generating tasks:', error);
      setErrorMessage(`Tasks could not be regenerated: ${getApolloErrorMessage(error)}`);
      setRegenerateConfirmOpen(false);
    }
  });

  const [enhanceWithAI, { loading: enhancing }] = useMutation(PREVIEW_ENHANCE_TASKS);

  const [commitAI, { loading: committing }] = useMutation(COMMIT_ENHANCE_TASKS, {
    onCompleted: (data) => {
      if (data?.commitEnhanceTasks) {
        setAiDialog({ status: 'closed' });
        setSuccessMessage('Suggestions applied');
        onTasksUpdated?.();
      } else {
        setAiDialog({ status: 'error', message: 'Failed to apply the suggestions. Please try again.' });
      }
    },
    onError: (error) => {
      console.error('Error committing AI enhancements:', error);
      // e.g. "Tasks changed after this preview was generated. Preview again..."
      setAiDialog({ status: 'error', message: getApolloErrorMessage(error) });
    }
  });

  const handleRegenerateTasks = () => {
    if (!canChangeTasks || !project?.id) return;
    setRegenerateConfirmOpen(true);
  };

  const confirmRegenerateTasks = async () => {
    if (!canChangeTasks || !project?.id || generating) return;
    replacingRef.current = taskCount > 0;

    try {
      await generateTasks({
        variables: { projectId: project.id }
      });
    } catch (error) {
      console.error('Failed to regenerate tasks:', error);
      setErrorMessage(`Tasks could not be regenerated: ${getApolloErrorMessage(error)}`);
    }
  };

  /**
   * Opens the preview straight away in a loading state: the model takes tens
   * of seconds, and before the dialog only appeared once it finished, with a
   * snackbar for any failure.
   */
  const handleEnhanceWithAI = async () => {
    if (!canChangeTasks || !project?.id) return;
    const requestId = aiRequestRef.current + 1;
    aiRequestRef.current = requestId;
    setAiPreviewData(null);
    setAiDialog({ status: 'loading' });

    try {
      const { data } = await enhanceWithAI({ variables: { projectId: project.id } });
      if (aiRequestRef.current !== requestId) return;
      const preview = data?.previewEnhanceTasks;
      const suggestions = preview?.suggestions || [];
      // The preview resolver reports failures (no tasks yet, model error) as
      // an empty suggestion list with the reason in `raw`.
      if (suggestions.length === 0) {
        setAiDialog({ status: 'empty', message: preview?.raw || 'No suggestions came back. Please try again.' });
        return;
      }
      setAiPreviewData({ suggestions, raw: preview?.raw || '' });
      setAiDialog({ status: 'ready' });
    } catch (error) {
      if (aiRequestRef.current !== requestId) return;
      console.error('Error enhancing tasks with AI:', error);
      setAiDialog({ status: 'error', message: `Suggestions could not be prepared: ${getApolloErrorMessage(error)}` });
    }
  };

  const closeAIDialog = () => {
    aiRequestRef.current += 1;
    setAiDialog({ status: 'closed' });
  };

  // The dashboard's "Suggest task guidance": once per mount, and only when
  // there are tasks to enhance. If they are still being generated, wait for them.
  useEffect(() => {
    if (!autoOpenAIPreview || autoOpenHandledRef.current || !project?.id || tasksPending) return;
    autoOpenHandledRef.current = true;
    if (canChangeTasks) {
      if (taskCount > 0) {
        void handleEnhanceWithAI();
      } else {
        setNotice({
          message: 'Suggestions improve existing tasks. Generate tasks from the feedback first.',
          severity: 'info'
        });
      }
    }
    onAutoOpenAIPreviewHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenAIPreview, canChangeTasks, project?.id, tasksPending, taskCount]);

  const handleDownloadZip = async () => {
    if (!project?.id || !user) return;

    setExporting(true);
    try {
      const token = await user.getIdToken(true);
      const result = await generateProjectZip(project.id, token);
      if (!result?.success || !result.downloadUrl) {
        throw new Error(result?.message || 'ZIP generation failed');
      }
      const link = document.createElement('a');
      link.href = result.downloadUrl;
      link.download = result.filename || 'project.zip';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setSuccessMessage('Download started');
    } catch (error) {
      console.error('Failed to export ZIP:', error);
      setErrorMessage('The download could not be prepared. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleCommitAI = async () => {
    if (!canChangeTasks || !project?.id || committing) return;

    try {
      await commitAI({
        variables: { projectId: project.id }
      });
    } catch (error) {
      console.error('Error committing AI enhancements:', error);
    }
  };

  const timelineLabel = (task: any) => {
    const start = Number(task?.timeline?.start);
    const end = Number(task?.timeline?.end);
    if (!Number.isFinite(start)) return null;
    return Number.isFinite(end) && end > start
      ? `${formatTimecode(start)} – ${formatTimecode(end)}`
      : formatTimecode(start);
  };

  const generateTaskListMarkdown = () => {
    const tasks = project?.tasks || [];
    const projectName = project?.projectName || 'Untitled Project';
    const currentDate = new Date().toLocaleDateString();

    let markdown = `# ${projectName} - Task List\n\n`;
    markdown += `**Generated:** ${currentDate}\n`;
    markdown += `**Total Tasks:** ${tasks.length}\n`;
    markdown += `**Completed:** ${tasks.filter((t: any) => t.status === 'COMPLETED').length}\n`;
    markdown += `**In Progress:** ${tasks.filter((t: any) => t.status === 'IN_PROGRESS').length}\n`;
    markdown += `**Pending:** ${tasks.filter((t: any) => t.status === 'PENDING').length}\n\n`;

    if (tasks.length === 0) {
      markdown += '## No tasks found\n\n';
      return markdown;
    }

    // Group tasks by status
    const pendingTasks = tasks.filter((t: any) => t.status === 'PENDING');
    const inProgressTasks = tasks.filter((t: any) => t.status === 'IN_PROGRESS');
    const completedTasks = tasks.filter((t: any) => t.status === 'COMPLETED');

    const addTaskSection = (sectionTitle: string, taskList: any[]) => {
      if (taskList.length === 0) return '';

      markdown += `## ${sectionTitle} (${taskList.length})\n\n`;

      taskList.forEach((task, index) => {
        markdown += `### ${index + 1}. ${task.title}\n\n`;
        if (task.description) {
          markdown += `**Description:** ${task.description}\n\n`;
        }
        markdown += `- **Type:** ${task.type || 'General'}\n`;
        markdown += `- **Priority:** ${task.priority || 'Medium'}\n`;
        markdown += `- **Estimated Time:** ${task.estimatedMinutes || 0} minutes\n`;
        if (task.actualMinutes) {
          markdown += `- **Actual Time:** ${task.actualMinutes} minutes\n`;
        }
        const when = timelineLabel(task);
        if (when) {
          markdown += `- **Timeline:** ${when}\n`;
        }
        if (task.editorNotes) {
          markdown += `- **Editor Notes:** ${task.editorNotes}\n`;
        }
        if (task.assetRefs && task.assetRefs.length > 0) {
          markdown += `- **Assets:** ${task.assetRefs.join(', ')}\n`;
        }
        if (task.aiInstructions) {
          markdown += `- **AI Instructions:** ${task.aiInstructions}\n`;
        }
        markdown += `- **Created:** ${new Date(task.createdAt).toLocaleDateString()}\n`;
        if (task.completedAt) {
          markdown += `- **Completed:** ${new Date(task.completedAt).toLocaleDateString()}\n`;
        }
        markdown += '\n---\n\n';
      });
    };

    addTaskSection('Pending Tasks', pendingTasks);
    addTaskSection('In Progress Tasks', inProgressTasks);
    addTaskSection('Completed Tasks', completedTasks);

    return markdown;
  };

  const csvText = (value: unknown) => {
    let cell = value === null || value === undefined ? '' : String(value);
    // Neutralise spreadsheet formulas in free text.
    if (/^[=+\-@]/.test(cell)) cell = `'${cell}`;
    return `"${cell.replace(/"/g, '""')}"`;
  };

  const generateTaskListCSV = () => {
    const tasks = project?.tasks || [];

    const headers = [
      'Title',
      'Description',
      'Status',
      'Type',
      'Priority',
      'Estimated Minutes',
      'Actual Minutes',
      'Timeline Start',
      'Timeline End',
      'Editor Notes',
      'Asset References',
      'AI Instructions',
      'Created Date',
      'Completed Date'
    ];

    let csv = headers.join(',') + '\n';

    tasks.forEach((task: any) => {
      const row = [
        csvText(task.title),
        csvText(task.description),
        task.status || '',
        task.type || '',
        task.priority || '',
        task.estimatedMinutes || 0,
        task.actualMinutes || '',
        task.timeline?.start ?? '',
        task.timeline?.end ?? '',
        csvText(task.editorNotes),
        csvText(task.assetRefs?.join('; ')),
        csvText(task.aiInstructions),
        task.createdAt ? new Date(task.createdAt).toLocaleDateString() : '',
        task.completedAt ? new Date(task.completedAt).toLocaleDateString() : ''
      ];
      csv += row.join(',') + '\n';
    });

    return csv;
  };

  const handleExportTasks = (format: 'md' | 'csv') => {
    const projectName = project?.projectName || 'tasks';
    const timestamp = new Date().toISOString().slice(0, 10);

    if (format === 'md') {
      downloadTextFile(`${projectName}-tasks-${timestamp}.md`, generateTaskListMarkdown(), 'text/markdown;charset=utf-8');
    } else {
      downloadTextFile(`${projectName}-tasks-${timestamp}.csv`, generateTaskListCSV(), 'text/csv;charset=utf-8');
    }

    setSuccessMessage(`Tasks exported as ${format === 'md' ? 'Markdown' : 'CSV'}`);
  };

  const getTaskStats = () => {
    const tasks = project?.tasks || [];

    const completed = tasks.filter((t: any) => t.status === 'COMPLETED').length;
    const inProgress = tasks.filter((t: any) => t.status === 'IN_PROGRESS').length;
    const pending = tasks.filter((t: any) => t.status === 'PENDING').length;

    return { total: tasks.length, completed, inProgress, pending };
  };

  const stats = getTaskStats();
  const finishedTooltip = 'This project is finished, so its tasks can no longer change';
  const hasTasks = stats.total > 0;

  const generateButton = editable && (
    <Tooltip
      title={readOnly
        ? finishedTooltip
        : hasTasks ? 'Replace every task with a fresh set generated from the feedback' : 'Turn the creator’s time-coded feedback into tasks'}
    >
      <span>
        <Button
          // In the workspace toolbar the tab's one filled button is "Create task".
          variant={variant === 'toolbar' || hasTasks ? 'outlined' : 'contained'}
          size={variant === 'toolbar' ? 'small' : 'medium'}
          startIcon={generating ? <CircularProgress size={16} color="inherit" /> : hasTasks ? <RefreshIcon /> : <GenerateIcon />}
          onClick={hasTasks ? handleRegenerateTasks : () => void confirmRegenerateTasks()}
          disabled={generating || !canChangeTasks || tasksPending}
          color="primary"
        >
          {generating ? (hasTasks ? 'Regenerating…' : 'Generating…') : hasTasks ? 'Regenerate tasks' : 'Generate tasks from feedback'}
        </Button>
      </span>
    </Tooltip>
  );

  const enhanceButton = editable && (
    <Tooltip
      title={readOnly
        ? finishedTooltip
        : hasTasks ? 'See suggested guidance for each task before anything changes' : 'Generate tasks first'}
    >
      <span>
        <Button
          variant="outlined"
          size={variant === 'toolbar' ? 'small' : 'medium'}
          startIcon={enhancing ? <CircularProgress size={16} /> : <SuggestIcon />}
          onClick={() => void handleEnhanceWithAI()}
          disabled={enhancing || !canChangeTasks || !hasTasks || tasksPending}
        >
          Preview suggestions
        </Button>
      </span>
    </Tooltip>
  );

  const exportMenu = editable && (
    <>
      {variant === 'toolbar' ? (
        <Button
          size="small"
          variant="text"
          startIcon={<ExportIcon />}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          disabled={!hasTasks}
          aria-haspopup="menu"
        >
          Export
        </Button>
      ) : (
        <Tooltip title="Export the task list">
          <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size="small" aria-label="More task actions">
            <MoreIcon />
          </IconButton>
        </Tooltip>
      )}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem key="export-md" onClick={() => { handleExportTasks('md'); setAnchorEl(null); }}>
          <ExportIcon sx={{ mr: 1 }} fontSize="small" />
          Export as Markdown
        </MenuItem>
        <MenuItem key="export-csv" onClick={() => { handleExportTasks('csv'); setAnchorEl(null); }}>
          <ExportIcon sx={{ mr: 1 }} fontSize="small" />
          Export as CSV
        </MenuItem>
      </Menu>
    </>
  );

  return (
    <>
      {variant === 'toolbar' ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }} data-testid="task-actions">
          {(!editable || readOnly) && (
            <Chip label="Read only" size="small" />
          )}
          {generateButton}
          {enhanceButton}
          {exportMenu}
        </Stack>
      ) : (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" component="h2" sx={{ fontSize: '1rem', fontWeight: 700 }}>
              Tasks
            </Typography>

            {(!editable || readOnly) && (
              <Chip label="Read only" size="small" />
            )}
          </Box>

          {/* Task Statistics */}
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" spacing={2}>
              <Chip label={`${stats.total} total`} variant="outlined" size="small" />
              <Chip label={`${stats.completed} done`} variant="outlined" size="small" />
              <Chip label={`${stats.inProgress} in progress`} variant="outlined" size="small" />
              <Chip label={`${stats.pending} to do`} variant="outlined" size="small" />
            </Stack>
          </Box>

          {/* Action Buttons */}
          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 1 }}>
            {generateButton}
            {enhanceButton}
            {editable && (
              <Button
                variant="outlined"
                startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
                onClick={handleDownloadZip}
                disabled={exporting}
              >
                Download ZIP
              </Button>
            )}
            {exportMenu}
          </Stack>
        </Paper>
      )}

      {/* AI Preview: opens immediately, loading and errors shown inside */}
      <Dialog
        open={aiDialog.status !== 'closed'}
        onClose={() => { if (!committing) closeAIDialog(); }}
        maxWidth="md"
        fullWidth
        aria-labelledby="ai-preview-title"
      >
        <DialogTitle id="ai-preview-title" sx={{ fontWeight: 700 }}>
          Suggested task guidance
        </DialogTitle>
        <DialogContent>
          {aiDialog.status === 'loading' && (
            <Box sx={{ py: 3 }} role="status" aria-live="polite">
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', mb: 1.5 }}>
                {AI_PREVIEW_LOADING_TEXT}
              </Typography>
              <LinearProgress sx={{ mb: 1.5, height: 6, borderRadius: 3 }} />
              <Typography variant="body2" color="text.secondary">
                Each task is read against the creator&apos;s feedback. This usually takes 20–60 seconds.
                Nothing changes until you apply the suggestions.
              </Typography>
            </Box>
          )}

          {aiDialog.status === 'error' && (
            <Alert
              severity="error"
              sx={{ my: 1 }}
              action={(
                <Button color="inherit" size="small" onClick={() => void handleEnhanceWithAI()}>
                  Try again
                </Button>
              )}
            >
              {aiDialog.message}
            </Alert>
          )}

          {aiDialog.status === 'empty' && (
            <Alert severity="info" sx={{ my: 1 }}>
              {aiDialog.message}
            </Alert>
          )}

          {aiDialog.status === 'ready' && aiPreviewData && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Review the guidance below. Applying it adds exactly what you see here to each task; nothing is regenerated.
              </Typography>

              <Typography variant="subtitle2" component="h3" sx={{ fontWeight: 700, mb: 0.5 }}>
                Suggested guidance ({aiPreviewData.suggestions.length})
              </Typography>

              <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                {aiPreviewData.suggestions.map((task, index) => (
                  <Box
                    component="li"
                    key={index}
                    sx={{ py: 2, borderTop: index === 0 ? 'none' : `1px solid ${brand.border}` }}
                  >
                    <Typography sx={{ fontWeight: 700 }}>
                      {task.title}
                    </Typography>
                    {task.description && (
                      <Typography variant="body2" color="text.secondary">
                        {task.description}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5, fontVariantNumeric: 'tabular-nums' }}>
                      {[
                        taskTypeLabel(task.type),
                        `${formatPriorityLabel(task.priority)} priority`,
                        typeof task.estimatedMinutes === 'number' && task.estimatedMinutes > 0 ? `about ${task.estimatedMinutes} min` : null,
                        timelineLabel(task),
                      ].filter(Boolean).join(' · ')}
                    </Typography>
                    {/* The enhancement's actual output. Only title, description, type
                        and priority were rendered before — none of which the
                        suggestions change — so the preview showed nothing new. */}
                    {task.aiInstructions ? (
                      <Box sx={{ mt: 1, pl: 1.5, borderLeft: `3px solid ${brand.borderStrong}` }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                          Suggested approach
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {task.aiInstructions}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        No new guidance for this task.
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>

              {aiPreviewData.raw && (
                <Box sx={{ mt: 1, pt: 2, borderTop: `1px solid ${brand.border}` }}>
                  <Typography variant="subtitle2" component="h3" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Summary
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                    {aiPreviewData.raw}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeAIDialog} disabled={committing}>
            {aiDialog.status === 'ready' ? 'Cancel' : 'Close'}
          </Button>
          {canChangeTasks && aiDialog.status === 'ready' && (
            <Button
              onClick={handleCommitAI}
              variant="contained"
              disabled={committing}
              startIcon={committing ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {committing ? 'Applying…' : 'Apply suggestions'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Success / info Snackbar */}
      <Snackbar
        open={!!notice}
        autoHideDuration={6000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setNotice(null)} severity={notice?.severity || 'success'} sx={{ width: '100%' }}>
          {notice?.message}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setErrorMessage(null)} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* Regenerate tasks: asks first, because it replaces every task. */}
      <Dialog
        open={regenerateConfirmOpen}
        onClose={() => { if (!generating) setRegenerateConfirmOpen(false); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Regenerate tasks?
        </DialogTitle>
        <DialogContent>
          {generating ? (
            <Box sx={{ py: 2 }} role="status" aria-live="polite">
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                Regenerating tasks from the feedback…
              </Typography>
              <LinearProgress sx={{ height: 6, borderRadius: 3 }} />
            </Box>
          ) : (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This replaces every task with a fresh set generated from the creator’s feedback.
              </Alert>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Current tasks: <strong>{stats.total}</strong> ({stats.completed} done, {stats.inProgress} in progress, {stats.pending} to do).
              </Typography>
              <Typography variant="body2">
                Their status, your notes, time tracking and any edits will be permanently deleted.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setRegenerateConfirmOpen(false)} disabled={generating}>
            Cancel
          </Button>
          <Button
            onClick={confirmRegenerateTasks}
            variant="contained"
            color="error"
            disabled={generating || !canChangeTasks}
            startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
          >
            {generating ? 'Regenerating…' : 'Replace all tasks'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
